import { useState, useRef } from 'react'
import { motion } from 'framer-motion'
import SkillRetention from './SkillRetention'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { toast } from 'react-hot-toast'

export default function SkillCard({ skill, onEdit, onDelete, onAddSubtopic, onOpenNotes, onOpenEditSubtopic }) {
  const [isHovered, setIsHovered] = useState(false)
  const [expandedSubtopic, setExpandedSubtopic] = useState(null)
  const [showRetention, setShowRetention] = useState(false)
  const notesPreviewRef = useRef(null)
  
  // Ensure skill and subtopics are properly initialized
  const safeSkill = {
    ...skill,
    subtopics: Array.isArray(skill?.subtopics) ? skill.subtopics : []
  }
  
  // Calculate average progress with safe checks
  const averageProgress = safeSkill.subtopics.length 
    ? Math.round(safeSkill.subtopics.reduce((acc, subtopic) => acc + (subtopic.progress || 0), 0) / safeSkill.subtopics.length)
    : 0

  // Handle skill updates
  const handleSkillUpdate = (updatedSkill, options = {}) => {
    if (options.isReviewUpdate) {
      // If this is a review update, just update the skill data without showing the edit modal
      onEdit(updatedSkill, { skipModal: true });
    } else {
      // For regular updates, show the edit modal
      onEdit(updatedSkill);
    }
  };

  // Handle opening notes modal
  const handleOpenNotes = (e, subtopic) => {
    e.stopPropagation(); // Prevent event bubbling
    onOpenNotes(safeSkill, subtopic);
  };

  // Add subtopic edit button handler
  const handleOpenEditSubtopic = (e, subtopic) => {
    e.stopPropagation();
    onOpenEditSubtopic(safeSkill, subtopic);
  };

  // Handle PDF export from SkillCard
  const handleExportPdf = async (e, subtopicName) => {
    e.stopPropagation(); // Prevent event bubbling
    console.log('SkillCard handleExportPdf called'); // Log when function is called
    console.log('notesPreviewRef.current:', notesPreviewRef.current); // Log the ref value

    if (!notesPreviewRef.current) {
      toast.error('Could not capture notes content.');
      return;
    }

    try {
      console.log('Capturing canvas from SkillCard...'); // Log before capture
      const canvas = await html2canvas(notesPreviewRef.current, {
        scale: 2, // Increase scale for better resolution
        useCORS: true, // Handle images if any (ensure CORS is allowed)
      });

      console.log('Canvas captured from SkillCard', canvas); // Log after capture
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      // Calculate image position to center horizontally
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10; // Start a little down from the top

      // Add title to PDF
      pdf.setFontSize(16);
      pdf.text(`Notes for ${subtopicName}`, pdfWidth / 2, 10, { align: 'center' });

      // Add image below the title
      pdf.addImage(imgData, 'PNG', imgX, imgY + 5, imgWidth * ratio, imgHeight * ratio);
      
      pdf.save(`${safeSkill?.name || 'skill'}_${subtopicName || 'subtopic'}_notes.pdf`);

      toast.success('Notes exported as PDF.');
    } catch (error) {
      console.error('Error exporting PDF from SkillCard:', error); // Log the error
      toast.error('Failed to export notes as PDF.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200'
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200'
      default:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200'
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200'
      case 'medium':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-200'
      default:
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200'
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      whileHover={{ scale: 1.02 }}
      className="card animate-fade-in"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {safeSkill.name || 'Untitled Skill'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">{safeSkill.category || 'Uncategorized'}</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(safeSkill)}
            className="p-2 text-gray-500 hover:text-primary-500 dark:text-gray-400 dark:hover:text-primary-400 transition-colors"
            title="Edit Skill"
          >
            ✏️
          </button>
          <button
            onClick={() => onDelete(safeSkill.id)}
            className="p-2 text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
            title="Delete Skill"
          >
            🗑️
          </button>
        </div>
      </div>

      {/* Progress Section */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">Overall Progress</span>
          <span className="text-primary-600 dark:text-primary-400 font-medium">
            {averageProgress}%
          </span>
        </div>
        <div className="progress-bar">
          <div
            className={`progress-bar-fill ${averageProgress === 100 ? 'bg-green-500 animate-pulse' : 'bg-primary-500'}`}
            style={{ width: `${averageProgress}%` }}
          />
        </div>
      </div>

      {/* Subtopics Section */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Subtopics</h4>
          <button
            onClick={() => onAddSubtopic(safeSkill)}
            className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            + Add Subtopic
          </button>
        </div>
        {safeSkill.subtopics.map((subtopic) => (
          <div key={subtopic.id} className="relative">
            <div
              className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={() => setExpandedSubtopic(expandedSubtopic === subtopic.id ? null : subtopic.id)}
            >
              <div className="flex-1">
                <h5 className="font-medium text-gray-900 dark:text-white">{subtopic.name || 'Untitled Subtopic'}</h5>
                <div className="flex gap-2 mt-1">
                  <span className={`badge ${getStatusColor(subtopic.status)}`}>
                    {subtopic.status || 'not_started'}
                  </span>
                  <span className={`badge ${getPriorityColor(subtopic.priority)}`}>
                    {subtopic.priority || 'medium'} priority
                  </span>
                  {subtopic.estimatedHours > 0 && (
                    <span className="badge badge-accent">
                      {subtopic.estimatedHours}h
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => handleOpenEditSubtopic(e, subtopic)}
                  className="p-1 text-gray-500 hover:text-primary-500 dark:text-gray-400 dark:hover:text-primary-400 transition-colors"
                  title="Edit Subtopic"
                >
                  ✏️
                </button>
                <button
                  className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                >
                  {expandedSubtopic === subtopic.id ? '▼' : '▶'}
                </button>
              </div>
            </div>

            {expandedSubtopic === subtopic.id && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-2 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div className="space-y-4">
                  {/* Progress Section */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Progress</span>
                      <span className="text-primary-600 dark:text-primary-400">{subtopic.progress || 0}%</span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className={`progress-bar-fill ${subtopic.progress === 100 ? 'bg-green-500 animate-pulse' : 'bg-primary-500'}`}
                        style={{ width: `${subtopic.progress || 0}%` }}
                      />
                    </div>
                  </div>

                  {/* Notes Preview Section */}
                  {subtopic.notes && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                      <div className="flex justify-between items-center mb-2">
                        <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300">Notes</h6>
                        <button
                          onClick={(e) => handleExportPdf(e, subtopic.name)}
                          className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          title="Share Notes as PDF"
                        >
                          Share as PDF
                        </button>
                      </div>
                      <div ref={notesPreviewRef} className="prose dark:prose-invert max-w-none text-sm prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-strong:text-gray-900 dark:prose-strong:text-white prose-code:text-primary-600 dark:prose-code:text-primary-400">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {subtopic.notes}
                        </ReactMarkdown>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Last updated: {new Date(subtopic.lastUpdated).toLocaleDateString()}
                      </div>
                    </div>
                  )}

                  {/* Resources Section */}
                  {Array.isArray(subtopic.resources) && subtopic.resources.length > 0 && (
                    <div>
                      <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Learning Resources</h6>
                      <div className="space-y-2">
                        {subtopic.resources.map((resource, resourceIndex) => (
                          <a
                            key={resourceIndex}
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                                {resource.type === 'video' ? (
                                  <svg className="w-4 h-4 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                ) : resource.type === 'article' ? (
                                  <svg className="w-4 h-4 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                                  </svg>
                                ) : resource.type === 'course' ? (
                                  <svg className="w-4 h-4 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-primary-600 dark:text-primary-400 group-hover:text-primary-700 dark:group-hover:text-primary-300 truncate">
                                  {resource.title}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {resource.url}
                                </p>
                                {resource.description && (
                                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                                    {resource.description}
                                  </p>
                                )}
                              </div>
                              <div className="flex-shrink-0">
                                <svg className="w-4 h-4 text-gray-400 group-hover:text-primary-500 dark:text-gray-500 dark:group-hover:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Last Studied Section */}
                  {subtopic.lastStudied && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Last studied: {new Date(subtopic.lastStudied).toLocaleDateString()}
                    </div>
                  )}

                  {/* Notes Action Button */}
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Are you sure you want to delete this subtopic?')) {
                          const updatedSubtopics = safeSkill.subtopics.filter(s => s.id !== subtopic.id);
                          onEdit({
                            ...safeSkill,
                            subtopics: updatedSubtopics
                          });
                        }
                      }}
                      className="px-3 py-1 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      Delete Subtopic
                    </button>
                    <button
                      onClick={(e) => handleOpenNotes(e, subtopic)}
                      className="btn-primary"
                    >
                      {subtopic.notes ? 'Edit Notes' : 'Add Notes'}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        ))}
      </div>

      {/* Notes Section */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">General Notes</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">{safeSkill.notes || 'No notes yet'}</p>
        {safeSkill.updatedAt && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Last updated: {new Date(safeSkill.updatedAt).toLocaleDateString()}
          </p>
        )}
      </div>

      {/* Retention Section */}
      <div className="relative">
        <button
          onClick={() => setShowRetention(!showRetention)}
          className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
        >
          {showRetention ? 'Hide Retention' : 'Show Retention'}
        </button>
        {showRetention && (
          <div className="mt-4">
            <SkillRetention 
              skill={safeSkill} 
              onUpdate={handleSkillUpdate} 
            />
          </div>
        )}
      </div>
    </motion.div>
  )
}
